import offlineDb, { SyncQueueItem } from './offlineDb';
import { resolveApiUrl } from '../config/environment';
import { getAuthHeaders, authenticatedFetch, isBackendUnavailable } from './api';
import logger from '../utils/logger';

type SyncStatusListener = (status: SyncStatus) => void;

export interface SyncStatus {
  online: boolean;
  syncing: boolean;
  pendingCount: number;
}

let _active = false;
let _listeners: SyncStatusListener[] = [];
let _status: SyncStatus = { online: navigator.onLine, syncing: false, pendingCount: 0 };
let _swRegistration: ServiceWorkerRegistration | null = null;

function notify() {
  for (const fn of _listeners) {
    try { fn({ ..._status }); } catch { /* ignore */ }
  }
}

async function updatePendingCount() {
  try {
    _status.pendingCount = await offlineDb.getSyncQueueCount();
  } catch {
    _status.pendingCount = 0;
  }
  notify();
}

async function drain() {
  if (_active || !navigator.onLine) return;
  _active = true;
  _status.syncing = true;
  notify();

  try {
    const queue = await offlineDb.getAllSyncQueue();
    if (!queue.length) return;

    for (const item of queue) {
      try {
        const url = resolveApiUrl(item.path);
        const res = await fetch(url, {
          method: item.method,
          credentials: 'include',
          headers: {
            ...(item.body ? { 'Content-Type': 'application/json' } : {}),
            ...getAuthHeaders(),
          },
          body: item.body ? JSON.stringify(item.body) : undefined,
        });

        if (res.status === 401) {
          // A replayed queued mutation came back 401. Don't trust a single
          // mutation to decide the whole session is dead — re-validate against
          // the live session before logging anyone out. Guards against a fresh
          // OAuth token race and against one poisoned queue item kicking the
          // user out on every "Sync now" tap.
          let verdict: 'alive' | 'dead' | 'inconclusive' = 'inconclusive';
          try {
            const profileRes = await authenticatedFetch('/api/auth/profile');
            if (profileRes.ok) verdict = 'alive';
            else if (profileRes.status === 401 || profileRes.status === 403) verdict = 'dead';
            // 503 / offline-shape / other → inconclusive: stop quietly, no logout
          } catch {
            /* network error → inconclusive */
          }

          if (verdict === 'alive') {
            // Session is valid → this item is stale/bad. Drop it so it stops
            // blocking the queue and re-firing this path, then keep draining.
            logger.log('🔁 Sync item rejected (401) but session is valid — dropping stale item', item.path);
            await offlineDb.removeSyncItem(item.qid!);
            continue;
          }
          if (verdict === 'dead') {
            window.dispatchEvent(new CustomEvent('tokenExpired', {
              detail: { message: 'Session expired during sync. Please log in again.' },
            }));
          }
          break; // dead or inconclusive → stop draining this pass
        }

        // A 502/503/504 means the backend is down (a deploy), not that the
        // mutation is bad — keep the item and stop this pass, or we'd silently
        // discard the user's write for the duration of every deploy.
        if (isBackendUnavailable(res.status)) {
          logger.warn(`🔁 Sync paused — backend unavailable (${res.status}). Queue preserved.`);
          break;
        }

        // Remove on success, or on any 4xx response (except 401 which is handled above
        // and 429 which is rate-limited). 4xx errors are permanent client-side failures
        // that will never succeed on retry — keeping them causes an infinite stuck queue.
        const isNonRetriable =
          res.ok ||
          (res.status >= 400 && res.status < 500 && res.status !== 429);
        if (isNonRetriable) {
          await offlineDb.removeSyncItem(item.qid!);
        }
      } catch {
        break;
      }
    }
  } finally {
    _active = false;
    _status.syncing = false;
    await updatePendingCount();
    if (_status.pendingCount === 0) {
      window.dispatchEvent(new Event('syncComplete'));
    }
  }
}

function handleOnline() {
  _status.online = true;
  notify();
  drain();
}

function handleOffline() {
  _status.online = false;
  notify();
}

function handleSwMessage(e: MessageEvent) {
  if (e.data && e.data.type === 'NICE_SYNC') {
    drain();
  }
}

// A deploy takes the backend down without ever dropping the device's
// connectivity, so 'online' never fires and a paused queue would sit there
// indefinitely. AuthContext fires this once the backend answers again.
function handleBackendReconnected() {
  drain();
}

export function initSyncManager(swReg?: ServiceWorkerRegistration) {
  _swRegistration = swReg || null;
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  window.addEventListener('backendReconnected', handleBackendReconnected);
  if (navigator.serviceWorker) {
    navigator.serviceWorker.addEventListener('message', handleSwMessage);
  }
  _status.online = navigator.onLine;
  updatePendingCount();
}

export function destroySyncManager() {
  window.removeEventListener('online', handleOnline);
  window.removeEventListener('offline', handleOffline);
  window.removeEventListener('backendReconnected', handleBackendReconnected);
  if (navigator.serviceWorker) {
    navigator.serviceWorker.removeEventListener('message', handleSwMessage);
  }
}

export function onSyncStatus(listener: SyncStatusListener): () => void {
  _listeners.push(listener);
  listener({ ..._status });
  return () => {
    _listeners = _listeners.filter((l) => l !== listener);
  };
}

export function getSyncStatus(): SyncStatus {
  return { ..._status };
}

export async function enqueueOfflineMutation(
  method: string,
  path: string,
  body?: any
): Promise<void> {
  await offlineDb.addToSyncQueue({ method, path, body });
  if (_swRegistration && 'sync' in _swRegistration) {
    try {
      await (_swRegistration as any).sync.register('nice-sync');
    } catch { /* Background Sync not supported */ }
  }
  await updatePendingCount();
}

export async function forceDrain(): Promise<void> {
  return drain();
}
